import { createSelector } from '@reduxjs/toolkit';

import type { HealthRecords } from '@/domain/childNotebook/component/HealthInfo';
import type { RootState } from '@/store';
import type { PageJson } from '@/types/domain/childNotebook/entities/pageEntity';

import { newDateFromString, toStringDate } from '@/utils/date';

export const dateSelector = createSelector(
  (state: RootState) => state.childNotebook.date,
  (date) => date,
);

export const classChildSelector = createSelector(
  (state: RootState) => state.class.classChildList,
  (classChildList) => classChildList,
);

export const pageSelector = createSelector(
  [
    (state: RootState) => state.childNotebook.page,
    (state: RootState) => state.childNotebook.temperature,
    (state: RootState) => state.childNotebook.sleep,
    (state: RootState) => state.childNotebook.defecation,
    (state: RootState) => state.childNotebook.milk,
    (state: RootState) => state.childNotebook.meal,
    (state: RootState) => state.childNotebook.drug,
    (state: RootState) => state.childNotebook.date,
    (state: RootState) => state.child.childId,
  ],
  (
    page,
    temperature,
    sleep,
    defecation,
    milk,
    meal,
    drug,
    date,
    childId,
  ) => {
    if (date in page) {
      const ydt = toStringDate(newDateFromString(date, 0, -1));
      const isAfter1600 = (dtStr: string) => (new Date(dtStr)).getHours() >= 16;
      const data: {
        /** 親からのページ */
        pageApp?: PageJson | undefined,
        /** ページ */
        page?: PageJson | undefined,
        /** 健康記録 */
        healthRecords: HealthRecords,
      } = {
        pageApp: undefined,
        page: undefined,
        healthRecords: {
          temperatures: [
            ...(
              temperature[ydt]?.items
                .filter(
                  (v) => v.data.childId === childId
                    && isAfter1600(v.data.measureDateTime),
                )
                .reverse() || []
            ),
            ...(
              temperature[date]?.items
                .filter((v) => v.data.childId === childId)
                .reverse() || []
            ),
          ],
          sleeps: sleep[date]?.items.filter((v) => v.data.childId === childId).reverse() || [],
          defecations: [
            ...(
              defecation[ydt]?.items
                .filter(
                  (v) => v.data.childId === childId
                    && isAfter1600(v.data.measureDateTime),
                )
                .reverse() || []
            ),
            ...(
              defecation[date]?.items
                .filter((v) => v.data.childId === childId)
                .reverse() || []
            ),
          ],
          milks: [
            ...(
              milk[ydt]?.items
                .filter(
                  (v) => v.data.childId === childId
                    && isAfter1600(v.data.measureDateTime),
                )
                .reverse() || []
            ),
            ...(
              milk[date]?.items
                .filter((v) => v.data.childId === childId)
                .reverse() || []
            ),
          ],
          meals: [
            ...(
              meal[ydt]?.items
                .filter(
                  (v) => v.data.childId === childId
                    && isAfter1600(v.data.measureDateTime),
                )
                .reverse() || []
            ),
            ...(
              meal[date]?.items
                .filter((v) => v.data.childId === childId)
                .reverse() || []
            ),
          ],
          drugs: [
            ...(
              drug[ydt]?.items
                .filter(
                  (v) => v.data.childId === childId
                    && isAfter1600(v.data.measureDateTime),
                )
                .reverse() || []
            ),
            ...(
              drug[date]?.items
                .filter((v) => v.data.childId === childId)
                .reverse() || []
            ),
          ],
        },
      };
      // 睡眠の前後の日の追加処理
      const ydtSleep = sleep[ydt]?.items.filter((v) => v.data.childId === childId) || [];
      if (ydtSleep.length > 0 && ydtSleep[0].data.isSleepTime) {
        data.healthRecords.sleeps = [ydtSleep[0], ...data.healthRecords.sleeps];
      }
      const tdt = toStringDate(newDateFromString(date, 0, 1));
      const tdtSleep = sleep[tdt]?.items
        .filter((v) => v.data.childId === childId)
        .reverse() || [];
      if (tdtSleep.length > 0 && tdtSleep[tdtSleep.length - 1].data.isSleepTime === false) {
        data.healthRecords.sleeps = [...data.healthRecords.sleeps, tdtSleep[tdtSleep.length - 1]];
      }
      // ページの設定
      for (let i = 0; i < page[date].items.length; i += 1) {
        const item = page[date].items[i];
        if (item.data.childId === childId) {
          if (item.data.recorderType === 'app') {
            data.pageApp = item;
          } else {
            data.page = item;
          }
        }
      }
      return data;
    }
    return undefined;
  },
);
